import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Wordmark } from './Wordmark';
import { RealmMenu } from './RealmMenu';

const LINKS = [
  { label: 'Collections', menu: 'collections', hash: '' },
  { label: 'The Journey', section: 'realms' },
  { label: 'Artifacts', menu: 'artifacts', hash: '#artifacts' },
  { label: 'About', section: 'world' },
];

const NAV_MID = 44;

const readTheme = () => {
  let theme = 'dark';
  document.querySelectorAll('[data-nav-theme]').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top <= NAV_MID && r.bottom > NAV_MID) theme = el.dataset.navTheme;
  });
  return theme;
};

export const SiteNav = ({ interest }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [openMenu, setOpenMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [theme, setTheme] = useState('dark');

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      setScrolled(window.scrollY > 40);
      setTheme(readTheme());
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [pathname]);

  useEffect(() => {
    setMobileOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  const goSection = (id) => {
    setMobileOpen(false);
    if (pathname === '/') document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    else navigate(`/#${id}`, id === 'contact' && interest ? { state: { interest } } : undefined);
  };

  const testId = (label) => `nav-${label.toLowerCase().replace(/\s+/g, '-')}`;
  const light = theme === 'light';
  const themeClass = light ? 'nav--light' : 'nav--dark';

  return (
    <>
      <nav
        className={`site-nav fixed top-0 inset-x-0 z-50 ${themeClass} ${scrolled || mobileOpen ? 'site-nav--scrolled' : ''}`}
        data-theme={theme}
        data-testid="site-nav"
      >
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-[68px] md:h-[88px] flex items-center justify-between">
          <Wordmark />

          <div className="hidden md:flex items-center gap-8 lg:gap-10">
            {LINKS.map((link) =>
              link.menu ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setOpenMenu(link.menu)}
                  onMouseLeave={() => setOpenMenu(null)}
                >
                  <button
                    type="button"
                    className="nav-link"
                    aria-haspopup="menu"
                    aria-expanded={openMenu === link.menu}
                    onClick={() => setOpenMenu(openMenu === link.menu ? null : link.menu)}
                    data-testid={testId(link.label)}
                  >
                    {link.label}
                  </button>
                  {openMenu === link.menu && <RealmMenu hash={link.hash} onPick={() => setOpenMenu(null)} />}
                </div>
              ) : (
                <button key={link.label} type="button" className="nav-link" onClick={() => goSection(link.section)} data-testid={testId(link.label)}>
                  {link.label}
                </button>
              ),
            )}
            <button type="button" className="nav-link nav-link--cta" onClick={() => goSection('contact')} data-testid="nav-request-preview">
              Request Preview
            </button>
          </div>

          <button
            type="button"
            className="nav-burger md:hidden p-2 -mr-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            data-testid="nav-mobile-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" strokeWidth={1.25} /> : <Menu className="w-5 h-5" strokeWidth={1.25} />}
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <div className={`mobile-menu fixed inset-0 z-40 pt-[68px] overflow-y-auto md:hidden ${themeClass}`} data-testid="mobile-menu">
          <div className="px-6 py-8 flex flex-col gap-2">
            <button type="button" className="mobile-link" onClick={() => goSection('realms')} data-testid="mobile-nav-the-journey">The Journey</button>
            <button type="button" className="mobile-link" onClick={() => goSection('world')} data-testid="mobile-nav-about">About</button>
            <button type="button" className="mobile-link mobile-link--cta" onClick={() => goSection('contact')} data-testid="mobile-nav-request-preview">Request Preview</button>
            <p className="mobile-eyebrow mt-8">Collections</p>
            <RealmMenu className="realm-menu--inline" onPick={() => setMobileOpen(false)} />
          </div>
        </div>
      )}
    </>
  );
};
